import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {getDate} from "../../redux/slice/dateSlice";
import {useNavigate} from "react-router-dom";
import {Search} from "lucide-react";
import {
    getCompleteServicesByCodeRate,
    getFeedbackRate,
    getPendingCount,
    getRequestDoneCountByCode,
    getRequestDoneCountByHandlerDept,
} from "../../api/static/StaticRequestService";
import ChartLoadingOverlay from "../../components/Utils/ChartLoadingOverlay";
import {buildHandleChartRows, HandleChartTooltip} from "./TaskHandleCurrentStatic";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import TabChartHeader from "../../components/tabs/TabChartHeader";
import {setValue} from "../../redux/slice/dateSlice";
import {
    ComplyRateGauge,
} from "./TaskComplyRateStatic";

const SERVICE_REQUEST_TABS = [
    "전체건수",
    "신청결재",
    "접수등록",
    "처리중",
    "처리완료",
    "미처리건",
];

const calcPercent = (cnt, denominator) => {
    const total = Number(denominator) || 0;
    const done = Number(cnt) || 0;
    if (total === 0) return 0;
    return Math.round((done / total) * 100);
};

const formatCountLabel = (cnt, denominator) =>
    `${Number(cnt) || 0} / ${Number(denominator) || 0}`;

export const toLineChartPoints = (raw) => {
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    if (!list.length) return [];

    if (list[0]?.data) {
        return list[0].data.map((p) => ({
            name: String(p.x ?? ""),
            value: Number(p.y) || 0,
        }));
    }
    if (list[0]?.x != null) {
        return list.map((p) => ({
            name: String(p.x ?? ""),
            value: Number(p.y) || 0,
        }));
    }
    return list.map((p) => ({
        name: String(p.name ?? ""),
        value: Number(p.cnt) ?? Number(p.y) ?? 0,
    }));
};

const truncateLabel = (name, max = 5) => {
    if (!name) return "";
    return name.length > max ? `${name.slice(0, max)}…` : name;
};

function ProgressBar({percent}) {
    return (
        <div className="h-[8px] min-w-0 flex-1 overflow-hidden rounded-full bg-[#e8e8e8]">
            <div
                className="h-full rounded-full bg-[#3b82f6] transition-[width] duration-300"
                style={{width: `${Math.min(100, Math.max(0, percent))}%`}}
            />
        </div>
    );
}

function ChartTooltip({active, payload, label, valueLabel = "건수"}) {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-[4px] border border-[rgba(0,0,0,0.08)] bg-white px-3 py-2 text-[12px] shadow-sm">
            <div className="font-medium text-[#333]">{label}</div>
            <div className="text-[#666]">
                {valueLabel} : {Number(payload[0]?.value || 0).toLocaleString()}
            </div>
        </div>
    );
}

/** 업무별 처리현황 / 진행단계별 현황 (ManagedTaskStatic 셀렉트·프로그레스 스타일) */
export function ServiceProgressCard({data, dateKey, defaultTitle}) {
    const dispatch = useDispatch();
    const period = useSelector((state) => getDate(state, dateKey));
    const items = data?.data ?? [];
    const title = data?.title ?? defaultTitle;

    const handlePeriodChange = (event) => {
        dispatch(setValue({keys: dateKey, value: Number(event.target.value)}));
    };

    return (
        <div className="flex h-full min-h-0 w-full flex-col bg-white">
            <div className="flex shrink-0 items-center justify-between gap-3">
                <h3 className="text-[21px] font-bold leading-none text-[#333]">{title}</h3>
                <select
                    value={String(period)}
                    onChange={handlePeriodChange}
                    className="rounded-[6px] border border-[#d9d9d9] bg-white px-[12px] py-[6px] text-[12px] text-[#333] outline-none focus:border-[#3b82f6]"
                >
                    <option value="1">금월</option>
                    <option value="0">금일</option>
                </select>
            </div>

            <div className="mt-[10px] min-h-0 flex-1 space-y-[6px] overflow-y-auto overscroll-contain pr-1">
                {items.length > 0 ? (
                    items.map((item, index) => {
                        const percent = calcPercent(item.cnt, item.denominator);
                        return (
                            <div
                                key={`${item.name}-${index}`}
                                className="flex items-center gap-[8px] border-b border-[#f0f0f0] py-[8px] last:border-b-0"
                            >
                                <span className="w-[72px] shrink-0 truncate text-[13px] font-medium text-[#333]">
                                    {item.name}
                                </span>
                                <span className="w-[56px] shrink-0 text-center text-[12px] text-[#666]">
                                    {formatCountLabel(item.cnt, item.denominator)}
                                </span>
                                <ProgressBar percent={percent}/>
                                <span className="w-[36px] shrink-0 text-right text-[13px] font-semibold text-[#333]">
                                    {percent}%
                                </span>
                            </div>
                        );
                    })
                ) : (
                    <div className="flex h-full min-h-[120px] items-center justify-center text-[13px] text-[#9ca3af]">
                        데이터가 없습니다.
                    </div>
                )}
            </div>
        </div>
    );
}

function ServiceRequestTable({rows, tabName}) {
    const navigate = useNavigate();
    const dateLabel =
        tabName === "미처리건"
            ? "요청마감일"
            : tabName === "처리완료"
                ? "완료일시"
                : "접수일시";

    return (
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
            <table className="large-listChart-table w-full">
                <thead className="sticky top-0 z-[1] bg-white">
                    <tr>
                        <th className="large-listChart-table-th w-[40px]">순서</th>
                        <th className="large-listChart-table-th">요청제목</th>
                        <th className="large-listChart-table-th w-[90px]">담당자</th>
                        <th className="large-listChart-table-th w-[90px]">진행단계</th>
                        <th className="large-listChart-table-th w-[110px]">{dateLabel}</th>
                        <th className="large-listChart-table-th w-[40px]">관리</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, index) => (
                        <tr key={`${tabName}-${row.id ?? index}`}>
                            <td className="large-listChart-table-td">{index + 1}</td>
                            <td className="large-listChart-table-td overflow-ellipsis text-nowrap">
                                {row.title}
                            </td>
                            <td className="large-listChart-table-td overflow-ellipsis text-nowrap">
                                {row.userName}
                            </td>
                            <td className="large-listChart-table-td">{row.statusKor}</td>
                            <td className="large-listChart-table-td">
                                {row.dateTime?.substring(0, 10)}
                            </td>
                            <td className="large-listChart-table-td text-center">
                                <button
                                    type="button"
                                    className="inline-flex items-center justify-center text-[#64748b] hover:text-[#3b82f6]"
                                    onClick={() => navigate(`${row.link}/${row.id}`)}
                                    aria-label="상세보기"
                                >
                                    <Search size={14}/>
                                </button>
                            </td>
                        </tr>
                    ))}
                    {rows.length < 1 && (
                        <tr>
                            <td colSpan={6} className="h-[160px] text-center text-[#9ca3af]">
                                조회 내역이 없습니다.
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

/** 중앙 탭 + 서비스 요청 목록 (ManagedAssetStatic 탭 패턴) */
export function ServiceRequestTabs({tabMap}) {
    const [tabAttribute, setTabAttribute] = useState(
        SERVICE_REQUEST_TABS.map((name, index) => ({clicked: index === 0, name}))
    );

    const activeTab = tabAttribute.find((tab) => tab.clicked);
    const activeTabName = activeTab?.name ?? SERVICE_REQUEST_TABS[0];
    const rows = Array.isArray(tabMap?.[activeTabName]) ? tabMap[activeTabName] : [];

    return (
        <div className="managed-asset-static flex h-full min-h-0 w-full flex-col bg-white [&_*:focus]:outline-none">
            <div className="shrink-0">
                <TabChartHeader
                    title={activeTabName}
                    variant="split"
                    args={tabAttribute}
                    setArgs={setTabAttribute}
                />
            </div>
            <div className="flex min-h-0 flex-1">
                <ServiceRequestTable rows={rows} tabName={activeTabName}/>
            </div>
        </div>
    );
}

/** 종합처리율 — 게이지만 표시 */
export function ServiceTimelyRateCard({data, loading = false}) {
    const percent = useMemo(() => {
        const left = data?.left;
        if (!left) return 0;
        const denominator = Number(left.denominator) || 0;
        const cnt = Number(left.cnt) || 0;
        if (denominator === 0) return 0;
        return Math.round((cnt / denominator) * 100);
    }, [data?.left]);

    const gaugeTitle = data?.left?.title ?? "금월 적기 처리율";

    if (!data && loading) {
        return (
            <div className="relative flex h-full min-h-0 w-full flex-col bg-white">
                <ChartLoadingOverlay />
            </div>
        );
    }

    return (
        <div className="relative flex h-full min-h-0 w-full flex-col bg-white">
            {loading && <ChartLoadingOverlay />}
            <h3 className="mb-1 shrink-0 text-[21px] font-bold leading-none text-[#333]">
                {data?.title ?? "종합처리율"}
            </h3>
            <div className="flex min-h-0 flex-1">
                <ComplyRateGauge value={percent} title={gaugeTitle} centered />
            </div>
        </div>
    );
}

function ServiceTrendLineChart({points, maxY}) {
    const yMax = useMemo(() => {
        if (maxY != null) return maxY;
        const maxVal = Math.max(...(points ?? []).map((p) => p.value), 0);
        if (maxVal <= 0) return 10;
        return Math.ceil(maxVal * 1.1) || 10;
    }, [points, maxY]);

    if (!points.length) {
        return (
            <div className="flex h-full items-center justify-center bg-[#fafafa] text-[13px] text-[#999]">
                No Data.
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col">
            <div className="min-h-0 flex-1">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={points} margin={{top: 8, right: 12, left: 0, bottom: 0}}>
                        <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e8e8e8"/>
                        <XAxis
                            dataKey="name"
                            tick={{fontSize: 10, fill: "#888"}}
                            tickFormatter={(v) => truncateLabel(v, 4)}
                            interval={0}
                            height={28}
                            axisLine={{stroke: "#666", strokeWidth: 1}}
                            tickLine={false}
                        />
                        <YAxis
                            domain={[0, yMax]}
                            allowDecimals={false}
                            tick={{fontSize: 11, fill: "#888"}}
                            width={28}
                            axisLine={{stroke: "#666", strokeWidth: 1}}
                            tickLine={false}
                        />
                        <Tooltip content={<ChartTooltip valueLabel="값"/>}/>
                        <Line
                            type="monotone"
                            dataKey="value"
                            stroke="#6B5CA5"
                            strokeWidth={2}
                            strokeDasharray="6 4"
                            dot={{r: 4, fill: "#6B5CA5", stroke: "#fff", strokeWidth: 2}}
                            activeDot={{r: 5}}
                            isAnimationActive={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

/** 막대 차트 (ManagedAssetLineArea 톤) */
export function ServiceBarChartCard({title, items}) {
    const chartData = useMemo(() => [...(items ?? [])], [items]);
    const yMax = useMemo(() => {
        const maxCnt = Math.max(...chartData.map((d) => Number(d.cnt) || 0), 0);
        if (maxCnt <= 0) return 10;
        return Math.ceil(maxCnt * 1.15) || 10;
    }, [chartData]);

    return (
        <div className="flex h-full min-h-0 w-full flex-col bg-white [&_*:focus]:outline-none">
            <h3 className="mb-[10px] shrink-0 text-[21px] font-bold leading-none text-[#333]">
                {title}
            </h3>
            <div className="min-h-0 flex-1">
                {!chartData.some((row) => Number(row.cnt) > 0) ? (
                    <div className="flex h-full items-center justify-center bg-[#fafafa] text-[13px] text-[#999]">
                        No Data.
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData} margin={{top: 12, right: 16, left: 4, bottom: 0}}>
                            <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#e8e8e8"/>
                            <XAxis
                                dataKey="name"
                                tick={{fontSize: 10, fill: "#888"}}
                                tickFormatter={(v) => truncateLabel(v, 4)}
                                interval={0}
                                angle={-35}
                                textAnchor="end"
                                height={36}
                                axisLine={{stroke: "#666", strokeWidth: 1}}
                                tickLine={false}
                            />
                            <YAxis
                                domain={[0, yMax]}
                                allowDecimals={false}
                                tick={{fontSize: 11, fill: "#888"}}
                                width={32}
                                axisLine={{stroke: "#666", strokeWidth: 1}}
                                tickLine={false}
                            />
                            <Tooltip content={<ChartTooltip/>}/>
                            <Bar dataKey="cnt" fill="#67b7dc" radius={[4, 4, 0, 0]} maxBarSize={48}/>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
}

/** 라인 추이 카드 (3행) */
export function ServiceTrendLineCard({title, data, maxY}) {
    const points = useMemo(() => toLineChartPoints(data), [data]);

    return (
        <div className="flex h-full min-h-0 w-full flex-col bg-white [&_*:focus]:outline-none">
            <h3 className="mb-[10px] shrink-0 text-[21px] font-bold leading-none text-[#333]">
                {title}
            </h3>
            <div className="min-h-0 flex-1">
                <ServiceTrendLineChart points={points} maxY={maxY}/>
            </div>
        </div>
    );
}

const formatListDate = (createdAt) => {
    if (!createdAt || typeof createdAt !== "string") return "";
    return createdAt.length >= 16 ? createdAt.substring(5, 16) : createdAt.substring(0, 10);
};

const PROCESS_BAR_BLUE = "#3b82f6";
const PROCESS_BAR_TRACK = "#ececec";
const PROCESS_AXIS = "#666666";
const PROCESS_GRID = "#e0e0e0";
const PROCESS_Y_TICKS = [0, 20, 40, 60, 80, 100];

const truncateProcessLabel = (name, maxLen = 3) => {
    if (!name) return "";
    return name.length > maxLen ? `${name.slice(0, maxLen)}...` : name;
};

/** 부서별 — lineChart 없이 cnt 기준 (레거시 bar와 동일 데이터) */
const buildDeptChartRows = (list) => {
    const items = list ?? [];
    const max = Math.max(...items.map((i) => Number(i.cnt) || 0), 1);
    return items.map((item, index) => {
        const done = Number(item.cnt) || 0;
        return {
            id: item.id ?? index,
            fullName: item.name,
            label: truncateProcessLabel(item.name),
            percent: Math.round((done / max) * 100),
            done,
            total: max,
        };
    });
};

function ProcessTabButton({label, active, onClick}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-md border px-3 py-1 text-[13px] leading-tight transition-colors ${
                active
                    ? "border-[#3b82f6] text-[#3b82f6]"
                    : "border-[#d1d5db] text-[#9ca3af] hover:border-[#9ca3af]"
            }`}
        >
            {label}
        </button>
    );
}

const PROCESS_TABS = [
    {id: "byCode", label: "업무별"},
    {id: "byDept", label: "부서별"},
];

/** 업무별 요청처리 + 담당자 부서별 — 통합대시보드 TaskHandleCurrentStatic 패턴 */
export function ServiceProcessStatusStatic() {
    const [activeTabId, setActiveTabId] = useState(PROCESS_TABS[0].id);
    const [loading, setLoading] = useState(true);
    const [chartRows, setChartRows] = useState([]);
    const serviceSearch = useSelector((state) => getDate(state, "서비스요청 처리현황"));

    const loadChart = useCallback(async () => {
        setLoading(true);
        try {
            if (activeTabId === "byCode") {
                const response = await getRequestDoneCountByCode(serviceSearch);
                if (response?.status === 200) {
                    setChartRows(
                        buildHandleChartRows(
                            response.data?.data,
                            response.data?.lineChart
                        )
                    );
                } else {
                    setChartRows([]);
                }
            } else {
                const response = await getRequestDoneCountByHandlerDept();
                if (response?.status === 200) {
                    const items = Array.isArray(response.data?.data)
                        ? response.data.data
                        : Array.isArray(response.data)
                            ? response.data
                            : [];
                    setChartRows(buildDeptChartRows(items));
                } else {
                    setChartRows([]);
                }
            }
        } catch {
            setChartRows([]);
        } finally {
            setLoading(false);
        }
    }, [activeTabId, serviceSearch]);

    useEffect(() => {
        loadChart();
    }, [loadChart]);

    return (
        <div className="relative flex h-full min-h-0 flex-col">
            {loading && <ChartLoadingOverlay />}

            <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-2">
                <h3 className="text-[21px] font-bold text-[#111827]">처리현황</h3>
                <div className="flex flex-wrap gap-1.5">
                    {PROCESS_TABS.map((tab) => (
                        <ProcessTabButton
                            key={tab.id}
                            label={tab.label}
                            active={activeTabId === tab.id}
                            onClick={() => setActiveTabId(tab.id)}
                        />
                    ))}
                </div>
            </div>

            <div className="min-h-0 flex-1">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartRows}
                        margin={{top: 8, right: 8, left: 0, bottom: 4}}
                        barCategoryGap="18%"
                    >
                        <CartesianGrid
                            strokeDasharray="4 4"
                            stroke={PROCESS_GRID}
                            vertical={false}
                        />
                        <XAxis
                            dataKey="label"
                            angle={-35}
                            textAnchor="end"
                            interval={0}
                            height={30}
                            tickMargin={2}
                            tick={{fill: PROCESS_AXIS, fontSize: 11}}
                            axisLine={{stroke: PROCESS_AXIS, strokeWidth: 1}}
                            tickLine={false}
                        />
                        <YAxis
                            domain={[0, 100]}
                            ticks={PROCESS_Y_TICKS}
                            width={28}
                            tick={{fill: PROCESS_AXIS, fontSize: 11}}
                            axisLine={{stroke: PROCESS_AXIS, strokeWidth: 1}}
                            tickLine={false}
                        />
                        <Tooltip content={<HandleChartTooltip />} cursor={{fill: "transparent"}} />
                        <Bar
                            dataKey="percent"
                            fill={PROCESS_BAR_BLUE}
                            radius={[10, 10, 0, 0]}
                            maxBarSize={36}
                            background={{fill: PROCESS_BAR_TRACK, radius: [10, 10, 0, 0]}}
                            isAnimationActive={!loading}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

const TREND_SERIES = [
    {
        id: "codeRate",
        dataKey: "codeRate",
        label: "업무별",
        color: "#3b82f6",
        fetch: () => getCompleteServicesByCodeRate(1),
    },
    {
        id: "pending",
        dataKey: "pending",
        label: "미처리",
        color: "#f59e0b",
        fetch: () => getPendingCount(1),
    },
    {
        id: "feedback",
        dataKey: "feedback",
        label: "만족도",
        color: "#ef4444",
        fetch: () => getFeedbackRate(1),
    },
];

const TREND_VISIBILITY_INITIAL = Object.fromEntries(
    TREND_SERIES.map((s) => [s.dataKey, true])
);

/** 레거시 nivo 응답 → body.data[0].data */
const extractNivoPoints = (body) => {
    if (!body) return {points: [], stroke: "#6B5CA5"};

    const seriesList = Array.isArray(body)
        ? body
        : Array.isArray(body.data)
            ? body.data
            : [];

    const series = seriesList[0];
    if (!series?.data?.length) {
        return {points: [], stroke: series?.color ?? "#6B5CA5"};
    }

    return {
        points: series.data.map((p) => ({
            name: String(p.x ?? ""),
            value: Number(p.y) ?? Number(p.yFormatted) ?? 0,
        })),
        stroke: series.color ?? "#6B5CA5",
    };
};

const mergeTrendSeriesRows = (seriesResults) => {
    const order = [];
    const byName = new Map();

    for (const {dataKey, points} of seriesResults) {
        for (const {name, value} of points) {
            if (!byName.has(name)) {
                const row = {name};
                byName.set(name, row);
                order.push(name);
            }
            byName.get(name)[dataKey] = value;
        }
    }

    return order.map((name) => byName.get(name));
};

function TrendMultiTooltip({active, payload, label}) {
    if (!active || !payload?.length) return null;
    const rows = payload.filter((entry) => entry.value != null && entry.name);
    if (!rows.length) return null;

    return (
        <div className="rounded-md border border-[#e5e7eb] bg-white px-3 py-2 text-[12px] shadow-sm">
            <p className="mb-1 font-medium text-[#111827]">{label}</p>
            {rows.map((entry) => (
                <p key={entry.dataKey} className="text-[#374151]" style={{color: entry.color}}>
                    {entry.name}: {Number(entry.value).toLocaleString()}
                </p>
            ))}
        </div>
    );
}

function TrendSeriesLegend({visible, onToggle}) {
    return (
        <div className="flex shrink-0 flex-wrap items-center justify-center gap-4 border-t border-[#f0f0f0] pt-2">
            {TREND_SERIES.map((series) => (
                <label
                    key={series.id}
                    className="inline-flex cursor-pointer select-none items-center gap-1.5 text-[12px] text-[#374151]"
                >
                    <input
                        type="checkbox"
                        className="h-3.5 w-3.5 accent-[#3b82f6]"
                        checked={visible[series.dataKey]}
                        onChange={() => onToggle(series.dataKey)}
                    />
                    <span
                        className="inline-block h-3 w-3 shrink-0 rounded-sm"
                        style={{backgroundColor: series.color}}
                    />
                    {series.label}
                </label>
            ))}
        </div>
    );
}

/** 업무별 처리율 + 미처리 누적 + 만족도 — 단일 스플라인 영역 차트 */
export function ServiceTrendStatusStatic() {
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState([]);
    const [visible, setVisible] = useState(TREND_VISIBILITY_INITIAL);

    const loadChart = useCallback(async () => {
        setLoading(true);
        try {
            const results = await Promise.allSettled(TREND_SERIES.map((s) => s.fetch()));
            const merged = mergeTrendSeriesRows(
                TREND_SERIES.map((series, index) => {
                    const result = results[index];
                    if (result.status !== "fulfilled" || result.value?.status !== 200) {
                        return {dataKey: series.dataKey, points: []};
                    }
                    const {points} = extractNivoPoints(result.value.data);
                    return {dataKey: series.dataKey, points};
                })
            );
            setChartData(merged);
        } catch {
            setChartData([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadChart();
    }, [loadChart]);

    const toggleSeries = useCallback((dataKey) => {
        setVisible((prev) => ({...prev, [dataKey]: !prev[dataKey]}));
    }, []);

    const yDomain = useMemo(() => {
        const values = [];
        for (const row of chartData) {
            for (const series of TREND_SERIES) {
                if (!visible[series.dataKey]) continue;
                const v = row[series.dataKey];
                if (v != null && !Number.isNaN(Number(v))) values.push(Number(v));
            }
        }
        if (!values.length) return [0, 10];
        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);
        const pad = maxVal === minVal ? Math.max(Math.abs(maxVal) * 0.1, 1) : (maxVal - minVal) * 0.1;
        const yMin = minVal < 0 ? Math.floor(minVal - pad) : 0;
        const yMax = Math.ceil(maxVal + pad) || 10;
        return [yMin, yMax];
    }, [chartData, visible]);

    const hasData = chartData.some((row) =>
        TREND_SERIES.some((s) => row[s.dataKey] != null)
    );
    const hasVisibleSeries = TREND_SERIES.some((s) => visible[s.dataKey]);

    return (
        <div className="relative flex h-full min-h-0 flex-col [&_*:focus]:outline-none [&_.recharts-active-dot]:outline-none">
            {loading && <ChartLoadingOverlay />}

            <h3 className="mb-2 shrink-0 text-[21px] font-bold text-[#111827]">추이현황</h3>

            <div className="min-h-0 flex-1">
                {!hasData && !loading ? (
                    <div className="flex h-full items-center justify-center text-[13px] text-[#9ca3af]">
                        No Data.
                    </div>
                ) : !hasVisibleSeries ? (
                    <div className="flex h-full items-center justify-center text-[13px] text-[#9ca3af]">
                        표시할 항목을 선택해 주세요.
                    </div>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={chartData}
                            margin={{top: 8, right: 12, left: 0, bottom: 0}}
                        >
                            <defs>
                                {TREND_SERIES.map((series) => (
                                    <linearGradient
                                        key={series.id}
                                        id={`trend-area-${series.id}`}
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop offset="0%" stopColor={series.color} stopOpacity={0.35}/>
                                        <stop offset="100%" stopColor={series.color} stopOpacity={0.05}/>
                                    </linearGradient>
                                ))}
                            </defs>
                            <CartesianGrid strokeDasharray="4 4" stroke="#e8e8e8"/>
                            <XAxis
                                dataKey="name"
                                tick={{fontSize: 10, fill: "#888"}}
                                tickFormatter={(v) => truncateLabel(v, 4)}
                                interval={0}
                                height={28}
                                axisLine={{stroke: "#666", strokeWidth: 1}}
                                tickLine={false}
                            />
                            <YAxis
                                domain={yDomain}
                                allowDecimals
                                tick={{fontSize: 11, fill: "#888"}}
                                width={32}
                                axisLine={{stroke: "#666", strokeWidth: 1}}
                                tickLine={false}
                            />
                            <Tooltip content={<TrendMultiTooltip />} />
                            {TREND_SERIES.map((series) => (
                                <Area
                                    key={series.id}
                                    type="monotone"
                                    dataKey={series.dataKey}
                                    name={series.label}
                                    stroke={series.color}
                                    fill={`url(#trend-area-${series.id})`}
                                    strokeWidth={2}
                                    connectNulls
                                    hide={!visible[series.dataKey]}
                                    dot={false}
                                    activeDot={{r: 4}}
                                    isAnimationActive={!loading}
                                />
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>

            <TrendSeriesLegend visible={visible} onToggle={toggleSeries}/>
        </div>
    );
}

/** 공지/자료실 목록 (TotalBulletin 행 스타일) */
export function ServiceFeedList({title, items}) {
    const navigate = useNavigate();
    const rows = Array.isArray(items) ? items : [];

    return (
        <div className="flex h-full min-h-0 w-full flex-col bg-white">
            <h3 className="mb-2 shrink-0 text-[21px] font-bold leading-none text-[#111827]">
                {title}
            </h3>
            {rows.length > 0 ? (
                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                    {rows.map((item, index) => (
                        <button
                            key={item.id ?? index}
                            type="button"
                            onClick={() => item?.link && item?.id != null && navigate(`${item.link}/${item.id}`)}
                            className="flex w-full items-center justify-between gap-3 border-b border-[#eeeeee] py-3 text-left last:border-b-0 hover:bg-[#fafafa]"
                        >
                            <span className="min-w-0 flex-1 truncate text-[14px] text-[#111827]">
                                {item.subject ?? ""}
                            </span>
                            <span className="shrink-0 text-[13px] text-[#9ca3af]">
                                [{formatListDate(item.createdAt)}]
                            </span>
                        </button>
                    ))}
                </div>
            ) : (
                <div className="flex flex-1 items-center justify-center text-[13px] text-[#9ca3af]">
                    No Data.
                </div>
            )}
        </div>
    );
}
